import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogTitle, DialogContent, TextField, Button, DialogActions, Typography, Box, IconButton, Select, MenuItem, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Backdrop, CircularProgress } from '@mui/material';

const tiposRol = ["Administrador", "Empleado"];
const API_URL = process.env.REACT_APP_API_URL;

export default function DialogModifyUsuario({ openDialog, handleCloseDialog, data, handleUpdateData }) {
  const [nombre, setNombre] = useState("");
  const [rol, setRol] = useState("");
  const [contraseña, setContraseña] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitRef = React.useRef(false);

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    nombre: false,
    rol: false,
    // La contraseña es opcional, por lo que no la validamos como obligatoria
  });

  useEffect(() => {
    if (data) {
      setNombre(data.nombre || "");
      setRol(data.rol || "");
      // No se carga la contraseña por motivos de seguridad, se deja vacía
      setContraseña("");
    }
  }, [data]);

  const validateFields = () => {
    const errors = {
      nombre: nombre.trim() === "",
      rol: rol.trim() === "",
    };
    setFieldErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = async () => {
    if (submitRef.current || loading) return;
    if (!validateFields()) {
      setError("Por favor, complete todos los campos obligatorios.");
      return;
    }
    submitRef.current = true;
    setLoading(true);

    // Se arma el objeto con los datos actualizados
    const updatedData = {
      ...data,
      nombre: nombre,
      rol: rol,
    };

    // Si se ingresó una nueva contraseña, se agrega al objeto
    if (contraseña.trim() !== "") {
      updatedData.contraseña = contraseña;
    }

    try {
      const response = await axios.put(`${API_URL}/usuarios/${data.id}/`, updatedData);
      if (response.status === 200) {
        setLoading(false);
        handleUpdateData(response.data);
        handleCloseDialog();
      } else {
        setLoading(false);
        throw new Error("Error al actualizar los datos");
      }
    } catch (err) {
      setLoading(false);
      setError(err.message);
    } finally {
      submitRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={openDialog} onClose={handleCloseDialog}>
        <Backdrop
          open={loading}
          style={{ zIndex: 9999, color: '#fff' }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

      <DialogTitle>Modificar Usuario</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Nombre"
          fullWidth
          value={nombre}
          error={fieldErrors.nombre}
          helperText={fieldErrors.nombre ? "Este campo es obligatorio" : ""}
          onChange={(e) => setNombre(e.target.value)}
        />
        <Typography variant="subtitle2" sx={{ marginTop: 2 }}>Rol</Typography>
        <Select
          margin="dense"
          fullWidth
          value={rol}
          error={fieldErrors.rol}
          onChange={(e) => setRol(e.target.value)}
        >
          {tiposRol.map((tipo) => (
            <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
          ))}
        </Select>
        <TextField
          margin="dense"
          label="Contraseña"
          fullWidth
          type={showPassword ? "text" : "password"}
          value={contraseña}
          onChange={(e) => setContraseña(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText="Dejar en blanco si no desea cambiar la contraseña"
        />
      </DialogContent>
      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#d32f2f', padding: 1, borderRadius: 1, width: '95%' }}>
            <Typography sx={{ color: 'white', fontWeight: 'bold', marginRight: 1 }}>{error}</Typography>
            <IconButton color="inherit" onClick={() => setError("")}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      <DialogActions>
        <Button onClick={handleCloseDialog} variant="outlined" color='error'>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
